// src/hooks/useCityData.ts

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getCityData,
  type OverpassResponse,
} from '../utils/overpass';
import {
  parseOSMData,
  type ParsedOSMData,
  type LocalPoint,
} from '../utils/osmParser';
import { GeoConverter } from '../utils/geoConverter';
import {
  getCacheKey,
  loadRawFromCache,
  saveRawToCache,
} from '../utils/cache';
import { useCityStore } from '../store/useCityStore';

// ----------------------------------------------------------------------
// Tipos de retorno
// ----------------------------------------------------------------------

export interface BuildingForRender {
  id: number | string;
  outerRing: LocalPoint[];
  innerRings: LocalPoint[][];
  height: number;
  name?: string;
  buildingType?: string;
  color?: string;
  tags: Record<string, string>;
}

export interface RoadForRender {
  id: number;
  points: LocalPoint[];
  width: number;
  name?: string;
  highwayType: string;
  lanes: number;
  oneway: boolean;
}

export interface CityData {
  buildings: BuildingForRender[];
  roads: RoadForRender[];
  rawData?: ParsedOSMData;
}

export interface UseCityDataOptions {
  lat: number;
  lng: number;
  radiusMeters?: number;
  includeRoads?: boolean;
  autoLoad?: boolean;
  /** URL de un JSON local (por ejemplo public/data/*.json) para usar como semilla sin llamar a la API */
  seedUrl?: string;
}

export interface UseCityDataResult {
  data: CityData | null;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
  setCenter: (lat: number, lng: number) => void;
}

// ----------------------------------------------------------------------
// Hook principal
// ----------------------------------------------------------------------

/**
 * Hook para cargar y procesar datos de ciudad desde Overpass API.
 * Devuelve los edificios y calles convertidos a coordenadas locales (metros)
 * listos para renderizar en Three.js.
 *
 * @param options - Configuración: centro, radio, opciones
 * @returns { data, loading, error, reload, setCenter }
 */
export function useCityData(options: UseCityDataOptions): UseCityDataResult {
  const {
    lat,
    lng,
    radiusMeters = 500,
    includeRoads = true,
    autoLoad = true,
    seedUrl,
  } = options;

  const [rawResponse, setRawResponse] = useState<OverpassResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [center, setCenterState] = useState<{ lat: number; lng: number }>({
    lat,
    lng,
  });

  // Sincronizar centro cuando cambian las props lat/lng
  useEffect(() => {
    setCenterState({ lat, lng });
  }, [lat, lng]);

  // Referencia para cancelar solicitudes en curso
  const abortControllerRef = useRef<AbortController | null>(null);

  // Función para cargar datos
  const loadData = useCallback(
    async (forceApi = false) => {
      if (center.lat === 0 && center.lng === 0) return;

      setLoading(true);
      setError(null);

      const cacheKey = getCacheKey(center.lat, center.lng, radiusMeters);

      // 1. Si existe un JSON local del sitio, priorizarlo siempre y no ir a la API.
      if (seedUrl) {
        try {
          const res = await fetch(seedUrl);
          if (res.ok) {
            const seed = (await res.json()) as OverpassResponse;
            saveRawToCache(cacheKey, seed);
            setRawResponse(seed);
            setLoading(false);
            return;
          }
        } catch {
          setError(new Error('No se pudo cargar el archivo JSON del sitio.'));
          setRawResponse(null);
          setLoading(false);
          return;
        }
      }

      // 2. Si no hay JSON local, usar caché y, finalmente, la API solo cuando no haya semilla.
      if (!forceApi && !seedUrl) {
        const cached = loadRawFromCache(cacheKey);
        if (cached) {
          setRawResponse(cached);
          setLoading(false);
          return;
        }
      }

      if (seedUrl) {
        setError(new Error('El archivo JSON del sitio no está disponible.'));
        setRawResponse(null);
        setLoading(false);
        return;
      }

      // Cancelar solicitud anterior
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        const raw = await getCityData(center.lat, center.lng, radiusMeters);
        saveRawToCache(cacheKey, raw);
        setRawResponse(raw);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        setError(err as Error);
        setRawResponse(null);
      } finally {
        setLoading(false);
      }
    },
    [center.lat, center.lng, radiusMeters, seedUrl]
  );

  // Carga automática al montar o cuando cambia el centro
  useEffect(() => {
    if (autoLoad) {
      loadData();
    }
    // Limpiar al desmontar
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData, autoLoad]);

  // Función para actualizar el centro (dispara recarga)
  const setCenter = useCallback((newLat: number, newLng: number) => {
    setCenterState({ lat: newLat, lng: newLng });
  }, []);

  // Procesar datos cuando cambia la respuesta cruda
  const processedData = useMemo<CityData | null>(() => {
    if (!rawResponse) return null;

    try {
      // 2. Parsear respuesta OSM
      const parsed = parseOSMData(rawResponse);

      // 3. Crear conversor de coordenadas
      const converter = new GeoConverter(center.lat, center.lng);

      // 4. Convertir edificios
      const buildings: BuildingForRender[] = parsed.buildings.map((b) => ({
        id: b.id,
        outerRing: converter.latLngArrayToLocal(b.outerRing),
        innerRings: b.innerRings.map((ring) =>
          converter.latLngArrayToLocal(ring)
        ),
        height: b.height,
        name: b.name,
        buildingType: b.buildingType,
        tags: b.tags,
      }));

      // 5. Convertir calles
      const roads: RoadForRender[] = includeRoads
        ? parsed.roads.map((r) => ({
            id: r.id,
            points: converter.latLngArrayToLocal(r.coordinates),
            width: r.width || 8,
            name: r.name,
            highwayType: r.highwayType,
            lanes: r.lanes,
            oneway: r.oneway,
          }))
        : [];

      return { buildings, roads, rawData: parsed };
    } catch (err) {
      console.error('Error al procesar datos de la ciudad:', err);
      return null;
    }
  }, [rawResponse, center.lat, center.lng, includeRoads]);

  // Conectar los datos procesados con el store global (EditorUI)
  useEffect(() => {
    if (!processedData) return;
    useCityStore.getState().setBuildings(processedData.buildings);
    useCityStore.getState().setRoads(processedData.roads);
  }, [processedData]);

  return {
    data: processedData,
    loading,
    error,
    reload: () => loadData(false),
    setCenter,
  };
}

// ----------------------------------------------------------------------
// Hook auxiliar para convertir más datos si es necesario
// ----------------------------------------------------------------------

/**
 * Hook auxiliar que devuelve el conversor de coordenadas actual.
 */
export function useGeoConverter(lat: number, lng: number): GeoConverter {
  return useMemo(() => new GeoConverter(lat, lng), [lat, lng]);
}

// ----------------------------------------------------------------------
// Ejemplo de uso (comentado)
// ----------------------------------------------------------------------

/*
import { useCityData } from './hooks/useCityData';

function CityViewer() {
  const { data, loading, error, reload, setCenter } = useCityData({
    lat: 6.2558643,
    lng: -75.6016472,
    radiusMeters: 500,
  });

  if (loading) return <div>Cargando ciudad...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!data) return null;

  return (
    <div>
      <h3>Edificios: {data.buildings.length}</h3>
      <h3>Calles: {data.roads.length}</h3>
      <button onClick={reload}>Recargar</button>
      <button onClick={() => setCenter(6.2442, -75.5812)}>
        Cambiar centro
      </button>
    </div>
  );
}
*/